import React, { useState, useEffect } from "react";
import api from "../lib/api";


const fmt = (n: number, d = 0) =>
  n.toLocaleString("en-KE", { minimumFractionDigits: d, maximumFractionDigits: d });

/* ─── types ─────────────────────────────────────────────── */
type ReqStatus = "pending" | "approved" | "rejected" | "cancelled";

interface CreditPolicy {
  id: number;
  property: number;
  property_name: string;
  is_enabled: boolean;
  credit_price_per_unit: string;
  max_credit_amount: string;
  grace_period_days: number;
  late_fee_percent: string;
}

interface CreditAccount {
  id: number;
  meter: number;
  meter_serial: string;
  property_name: string;
  unit_number: string;
  credit_limit: string;
  outstanding_balance: string;
  available_credit: string;
  status: "active" | "suspended" | "closed";
  due_date: string | null;
}

interface CreditRequest {
  id: number;
  meter: number;
  meter_serial: string;
  property_name: string;
  unit_number: string;
  amount_requested: string;
  units_to_allocate: string | null;
  credit_price_per_unit: string;
  status: ReqStatus;
  rejection_reason: string;
  reviewed_at: string | null;
  created_at: string;
}

interface CreditRepayment {
  id: number;
  credit_account: number;
  meter_serial: string;
  amount: string;
  method: string;
  mpesa_receipt: string;
  created_at: string;
}

interface Meter {
  id: number;
  serial_number: string;
  unit_number: string;
  property_name: string;
}

/* ─── helpers ───────────────────────────────────────────── */
function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-xl ${className}`} />;
}

const STATUS_META: Record<ReqStatus, { label: string; bg: string; text: string; dot: string }> = {
  pending:   { label: "Pending Review", bg: "bg-amber-50",  text: "text-amber-600",  dot: "bg-amber-400" },
  approved:  { label: "Approved",       bg: "bg-emerald-50",text: "text-emerald-600",dot: "bg-emerald-400" },
  rejected:  { label: "Rejected",       bg: "bg-red-50",    text: "text-red-500",    dot: "bg-red-400" },
  cancelled: { label: "Cancelled",      bg: "bg-gray-100",  text: "text-gray-400",   dot: "bg-gray-300" },
};

const PRESET_AMOUNTS = [100, 200, 300, 500, 1000];

/* ─── request modal ──────────────────────────────────────── */
function RequestModal({
  meters,
  policies,
  onClose,
  onSuccess,
}: {
  meters: Meter[];
  policies: CreditPolicy[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [meterId, setMeterId] = useState<number | "">(meters[0]?.id ?? "");
  const [amount, setAmount] = useState(200);
  const [customAmount, setCustomAmount] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const meter = meters.find((m) => m.id === meterId);
  // find a matching policy via property name (simplification)
  const policy = policies.find((p) => p.property_name === meter?.property_name);
  const pricePerUnit = parseFloat(policy?.credit_price_per_unit ?? "1.50");
  const units = policy ? amount / pricePerUnit : 0;
  const maxCredit = parseFloat(policy?.max_credit_amount ?? "500");
  const creditEnabled = policy?.is_enabled ?? false;

  const handleSubmit = async () => {
    if (!meterId || !policy) { setError("Please select a meter."); return; }
    if (!creditEnabled) { setError("Credit billing is not enabled for this property."); return; }
    if (amount > maxCredit) { setError(`Maximum allowed is KES ${fmt(maxCredit)}.`); return; }
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/credit/requests/`, {
        meter: meterId, policy: policy.id, amount_requested: amount,
      });
      setSubmitted(true);
      setTimeout(() => { onSuccess(); onClose(); }, 2000);
    } catch (e: any) {
      const d = e.response?.data;
      setError(typeof d === "string" ? d : Object.values(d ?? {}).flat().join(" ") || "Request failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
        {/* header */}
        <div className="bg-gradient-to-r from-[#0f2557] to-[#0e2050] px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-white font-bold text-[15px]">Request Water on Credit</h2>
              <p className="text-sky-300/70 text-[12px] mt-0.5">Water now, pay later — subject to manager approval</p>
            </div>
            <button onClick={onClose} className="text-white/50 hover:text-white transition-colors">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
                <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        {submitted ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-emerald-50 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth={2} className="w-8 h-8">
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p className="font-bold text-gray-800 text-[16px]">Request submitted!</p>
            <p className="text-[13px] text-gray-400 mt-1">
              An SMS confirmation has been sent to your phone. Your manager will review shortly.
            </p>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            {/* meter select */}
            <div>
              <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-2">Select Meter</label>
              <select
                value={meterId}
                onChange={(e) => setMeterId(Number(e.target.value))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300"
              >
                {meters.map((m) => (
                  <option key={m.id} value={m.id}>{m.property_name} — Unit {m.unit_number} · {m.serial_number}</option>
                ))}
              </select>
              {policy && !creditEnabled && (
                <p className="text-[12px] text-red-500 mt-1.5">Credit billing is not enabled for this property.</p>
              )}
            </div>

            {/* amount */}
            <div>
              <label className="block text-[12px] font-semibold text-gray-600 uppercase tracking-widest mb-2">Amount (KES)</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {PRESET_AMOUNTS.filter((a) => a <= maxCredit).map((a) => (
                  <button
                    key={a}
                    onClick={() => { setAmount(a); setCustomAmount(false); }}
                    className={`px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border transition-all ${
                      amount === a && !customAmount ? "bg-sky-500 text-white border-sky-500 shadow-sm" : "bg-white text-gray-600 border-gray-200 hover:border-sky-300"
                    }`}
                  >
                    KES {a}
                  </button>
                ))}
                <button
                  onClick={() => setCustomAmount(true)}
                  className={`px-3 py-1.5 rounded-lg text-[12.5px] font-semibold border transition-all ${customAmount ? "bg-sky-500 text-white border-sky-500" : "bg-white text-gray-600 border-gray-200 hover:border-sky-300"}`}
                >
                  Custom
                </button>
              </div>
              {customAmount && (
                <input
                  type="number"
                  value={amount}
                  max={maxCredit}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-sky-300"
                />
              )}
            </div>

            {/* calculator */}
            {policy && creditEnabled && (
              <div className="bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-100 rounded-xl p-4 space-y-2">
                <p className="text-[11px] font-semibold text-sky-700 uppercase tracking-widest">Credit Summary</p>
                <div className="grid grid-cols-2 gap-y-1.5 text-[12.5px]">
                  <span className="text-gray-500">Amount on credit</span>
                  <span className="font-bold text-gray-800">KES {fmt(amount)}</span>
                  <span className="text-gray-500">Credit rate</span>
                  <span className="font-semibold text-gray-700">KES {pricePerUnit.toFixed(2)} / unit</span>
                  <span className="text-gray-500">Units you'll receive</span>
                  <span className="font-bold text-sky-600">{fmt(units, 1)} {policy ? "litres" : "units"}</span>
                  <span className="text-gray-500">Repay within</span>
                  <span className="font-semibold text-gray-700">{policy.grace_period_days} days</span>
                  <span className="text-gray-500">Max credit limit</span>
                  <span className="font-semibold text-gray-700">KES {fmt(maxCredit)}</span>
                </div>
              </div>
            )}

            {/* disclaimer */}
            <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 text-[12px] text-amber-800 leading-relaxed">
              <strong>⚠️ Important:</strong> Credit water is charged at a <strong>higher rate</strong> (KES {pricePerUnit.toFixed(2)}/unit vs regular price). You must repay within {policy?.grace_period_days ?? 30} days or your account may be suspended.
            </div>

            {error && (
              <p className="text-[12.5px] text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
            )}

            <div className="flex gap-3 pt-1">
              <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[13px] font-semibold text-gray-600 hover:bg-gray-50 transition-all">
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !creditEnabled}
                className="flex-1 py-2.5 rounded-xl bg-sky-500 text-white text-[13px] font-semibold hover:bg-sky-600 transition-all shadow-sm disabled:opacity-60"
              >
                {submitting ? "Submitting…" : "Submit Request"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── request card ───────────────────────────────────────── */
function RequestCard({ req }: { req: CreditRequest }) {
  const meta = STATUS_META[req.status];
  const price = parseFloat(req.credit_price_per_unit);
  const units = req.units_to_allocate ? parseFloat(req.units_to_allocate) : parseFloat(req.amount_requested) / price;

  return (
    <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${req.status === "approved" ? "border-emerald-100" : req.status === "rejected" ? "border-red-100" : req.status === "pending" ? "border-amber-100" : "border-gray-100"}`}>
      <div className={`h-1 ${req.status === "approved" ? "bg-emerald-400" : req.status === "rejected" ? "bg-red-400" : req.status === "pending" ? "bg-amber-400" : "bg-gray-200"}`} />
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[13px] font-bold text-gray-800">{req.property_name} — Unit {req.unit_number}</p>
            <p className="text-[11.5px] text-gray-400 font-mono mt-0.5">{req.meter_serial}</p>
          </div>
          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${meta.bg} ${meta.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-gray-50 rounded-xl px-3 py-2">
            <p className="text-[10.5px] text-gray-400 uppercase tracking-wider">Amount</p>
            <p className="text-[14px] font-bold text-gray-800">KES {fmt(parseFloat(req.amount_requested))}</p>
          </div>
          <div className="bg-gray-50 rounded-xl px-3 py-2">
            <p className="text-[10.5px] text-gray-400 uppercase tracking-wider">Units</p>
            <p className="text-[14px] font-bold text-sky-600">{fmt(units, 1)} L</p>
          </div>
        </div>

        {req.status === "rejected" && req.rejection_reason && (
          <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-3 text-[12px] text-red-600">
            <strong>Reason:</strong> {req.rejection_reason}
          </div>
        )}

        <div className="flex items-center justify-between text-[11.5px] text-gray-400">
          <span>Ref: #CR{String(req.id).padStart(5, "0")}</span>
          <span>{new Date(req.created_at).toLocaleDateString("en-KE", { dateStyle: "medium" })}</span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════ */
export default function CreditBillingPage() {
  const [loading, setLoading] = useState(true);
  const [meters, setMeters] = useState<Meter[]>([]);
  const [policies, setPolicies] = useState<CreditPolicy[]>([]);
  const [accounts, setAccounts] = useState<CreditAccount[]>([]);
  const [requests, setRequests] = useState<CreditRequest[]>([]);
  const [repayments, setRepayments] = useState<CreditRepayment[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [tab, setTab] = useState<"overview" | "history" | "repayments">("overview");
  const [statusFilter, setStatusFilter] = useState<"all" | ReqStatus>("all");

  const load = async () => {
    setLoading(true);
    try {
      const [mRes, pRes, aRes, rRes, repRes] = await Promise.allSettled([
        api.get(`/meters/`),
        api.get(`/credit/policies/`),
        api.get(`/credit/accounts/`),
        api.get(`/credit/requests/`),
        api.get(`/credit/repayments/`),
      ]);
      if (mRes.status === "fulfilled") setMeters(mRes.value.data?.results ?? mRes.value.data ?? []);
      if (pRes.status === "fulfilled") setPolicies(pRes.value.data?.results ?? pRes.value.data ?? []);
      if (aRes.status === "fulfilled") setAccounts(aRes.value.data?.results ?? aRes.value.data ?? []);
      if (rRes.status === "fulfilled") setRequests(rRes.value.data?.results ?? rRes.value.data ?? []);
      if (repRes.status === "fulfilled") setRepayments(repRes.value.data?.results ?? repRes.value.data ?? []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totalOwed = accounts.reduce((s, a) => s + parseFloat(a.outstanding_balance), 0);
  const pendingCount = requests.filter((r) => r.status === "pending").length;
  const approvedCount = requests.filter((r) => r.status === "approved").length;
  const creditEnabled = policies.some((p) => p.is_enabled);

  const filteredRequests = statusFilter === "all" ? requests : requests.filter((r) => r.status === statusFilter);

  const TABS = [
    { key: "overview",   label: "Overview" },
    { key: "history",    label: "Request History" },
    { key: "repayments", label: "Repayments" },
  ] as const;

  const STATUS_TABS: { key: "all" | ReqStatus; label: string }[] = [
    { key: "all",       label: "All" },
    { key: "pending",   label: "Pending" },
    { key: "approved",  label: "Approved" },
    { key: "rejected",  label: "Rejected" },
    { key: "cancelled", label: "Cancelled" },
  ];

  return (
    <>
      <div className="space-y-7">

        {/* ── header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-[22px] font-bold text-gray-800 leading-tight">Credit Billing</h1>
            <p className="text-[13px] text-gray-400 mt-0.5">Request water on credit — get water now, pay later</p>
          </div>
          {creditEnabled && (
            <button
              onClick={() => setModalOpen(true)}
              className="flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold px-4 py-2.5 rounded-xl shadow-sm transition-all"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                <path d="M12 5v14M5 12h14" strokeLinecap="round"/>
              </svg>
              Request Credit Water
            </button>
          )}
        </div>

        {/* ── info banner ── */}
        {!creditEnabled && !loading && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 flex items-start gap-3">
            <span className="text-xl mt-0.5">⚠️</span>
            <div>
              <p className="text-[13px] font-bold text-amber-800">Credit billing is not enabled</p>
              <p className="text-[12.5px] text-amber-700/80 mt-0.5">
                Your property manager has not yet enabled credit billing. Contact them to request access.
              </p>
            </div>
          </div>
        )}

        {/* ── KPI strip ── */}
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Outstanding Balance", value: `KES ${fmt(totalOwed, 2)}`, sub: "Total owed across meters", color: totalOwed > 0 ? "bg-red-50" : "bg-emerald-50", stroke: totalOwed > 0 ? "#ef4444" : "#10b981" },
              { label: "Pending Requests", value: pendingCount.toString(), sub: "Awaiting manager review", color: "bg-amber-50", stroke: "#f59e0b" },
              { label: "Approved Requests", value: approvedCount.toString(), sub: "All time", color: "bg-emerald-50", stroke: "#10b981" },
              { label: "Credit Accounts", value: accounts.length.toString(), sub: "Active accounts", color: "bg-sky-50", stroke: "#0ea5e9" },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${s.color}`}>
                  <svg viewBox="0 0 24 24" fill="none" stroke={s.stroke} strokeWidth={1.8} className="w-5 h-5">
                    <rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20M6 15h4" strokeLinecap="round"/>
                  </svg>
                </div>
                <div>
                  <p className="text-[11.5px] text-gray-400 font-medium uppercase tracking-widest">{s.label}</p>
                  <p className="text-[20px] font-bold text-gray-800 leading-tight mt-0.5">{s.value}</p>
                  <p className="text-[11.5px] text-gray-400 mt-0.5">{s.sub}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── credit account cards ── */}
        {accounts.length > 0 && (
          <div>
            <h2 className="text-[14px] font-bold text-gray-700 mb-3">My Credit Accounts</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {accounts.map((acct) => {
                const owed = parseFloat(acct.outstanding_balance);
                const limit = parseFloat(acct.credit_limit);
                const pct = limit > 0 ? Math.min(100, (owed / limit) * 100) : 0;
                const barColor = pct >= 80 ? "bg-red-400" : pct >= 50 ? "bg-amber-400" : "bg-emerald-400";
                return (
                  <div key={acct.id} className={`bg-white rounded-2xl border shadow-sm p-5 ${acct.status === "suspended" ? "border-red-100 opacity-80" : "border-gray-100"}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="text-[13.5px] font-bold text-gray-800">{acct.property_name}</p>
                        <p className="text-[12px] text-gray-400">Unit {acct.unit_number} · <span className="font-mono">{acct.meter_serial}</span></p>
                      </div>
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${acct.status === "active" ? "bg-emerald-50 text-emerald-600" : acct.status === "suspended" ? "bg-red-50 text-red-500" : "bg-gray-100 text-gray-400"}`}>
                        {acct.status}
                      </span>
                    </div>
                    <div className="mb-3">
                      <div className="flex justify-between text-[12px] mb-1.5">
                        <span className="text-gray-500">Used credit</span>
                        <span className="font-semibold text-gray-800">KES {fmt(owed, 2)} / {fmt(limit, 2)}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div className={`h-2 rounded-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[12px]">
                      <div className="bg-sky-50 rounded-lg px-3 py-2">
                        <p className="text-gray-400 text-[10.5px] uppercase">Available</p>
                        <p className="font-bold text-sky-600">KES {fmt(parseFloat(acct.available_credit), 2)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg px-3 py-2">
                        <p className="text-gray-400 text-[10.5px] uppercase">Due Date</p>
                        <p className="font-bold text-gray-800">{acct.due_date ? new Date(acct.due_date).toLocaleDateString("en-KE", { dateStyle: "medium" }) : "—"}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── tab navigation ── */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-[12.5px] font-semibold transition-all ${tab === t.key ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW ── */}
        {tab === "overview" && (
          <div className="space-y-5">
            {/* how it works */}
            <div className="bg-gradient-to-r from-sky-500 to-blue-600 rounded-2xl p-5 text-white">
              <p className="font-bold text-[15px] mb-3">How Credit Billing Works</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { step: "1", icon: "📝", title: "Request", desc: "Submit a credit request with the amount you need." },
                  { step: "2", icon: "✅", title: "Approval", desc: "Your property manager reviews and approves." },
                  { step: "3", icon: "💧", title: "Water Flows", desc: "Tokens are loaded automatically — water starts." },
                  { step: "4", icon: "💳", title: "Repay", desc: "Repay within the grace period to keep your account active." },
                ].map((s) => (
                  <div key={s.step} className="bg-white/15 rounded-xl p-3">
                    <div className="text-2xl mb-1.5">{s.icon}</div>
                    <p className="font-bold text-[12.5px]">{s.title}</p>
                    <p className="text-sky-100/70 text-[11.5px] mt-0.5 leading-relaxed">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* recent requests */}
            {requests.length > 0 ? (
              <div>
                <h2 className="text-[14px] font-bold text-gray-700 mb-3">Recent Requests</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {requests.slice(0, 6).map((r) => <RequestCard key={r.id} req={r} />)}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center">
                <div className="text-3xl mb-2">💳</div>
                <p className="font-semibold text-gray-700 text-[14px]">No credit requests yet</p>
                <p className="text-[12.5px] text-gray-400 mt-1">When credit billing is enabled, you can request water here.</p>
              </div>
            )}
          </div>
        )}

        {/* ── HISTORY ── */}
        {tab === "history" && (
          <div className="space-y-4">
            <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
              {STATUS_TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setStatusFilter(t.key)}
                  className={`px-3.5 py-1.5 rounded-lg text-[12px] font-semibold transition-all ${statusFilter === t.key ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {t.label}
                  {t.key !== "all" && (
                    <span className="ml-1.5 text-[10.5px] bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-md">
                      {requests.filter((r) => r.status === t.key).length}
                    </span>
                  )}
                </button>
              ))}
            </div>
            {filteredRequests.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center">
                <p className="text-[14px] font-semibold text-gray-600">No requests found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredRequests.map((r) => <RequestCard key={r.id} req={r} />)}
              </div>
            )}
          </div>
        )}

        {/* ── REPAYMENTS ── */}
        {tab === "repayments" && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50">
              <h2 className="text-[14px] font-bold text-gray-800">Repayment History</h2>
              <p className="text-[12px] text-gray-400 mt-0.5">All credit repayments recorded by your property manager</p>
            </div>
            {repayments.length === 0 ? (
              <div className="p-10 text-center text-[13px] text-gray-400">No repayments recorded yet.</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {repayments.map((rep) => (
                  <div key={rep.id} className="flex items-center gap-4 px-5 py-3.5">
                    <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth={1.8} className="w-4 h-4">
                        <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-gray-800">
                        KES {fmt(parseFloat(rep.amount), 2)} · <span className="capitalize font-normal text-gray-500">{rep.method}</span>
                      </p>
                      <p className="text-[11.5px] text-gray-400">
                        Meter {rep.meter_serial}{rep.mpesa_receipt ? ` · ${rep.mpesa_receipt}` : ""}
                      </p>
                    </div>
                    <span className="text-[12px] text-gray-500 flex-shrink-0">
                      {new Date(rep.created_at).toLocaleDateString("en-KE", { dateStyle: "medium" })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      {modalOpen && (
        <RequestModal
          meters={meters}
          policies={policies}
          onClose={() => setModalOpen(false)}
          onSuccess={load}
        />
      )}
    </>
  );
}
